import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { Popup } from 'semantic-ui-react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { CalendarDisplayEvent } from '../../types/Calendar';
import {
    MONTHS_SHORT,
    WEEKDAYS,
    CalendarLayoutMode,
    getDateKey,
    getWeekStart,
} from '../../pages/calendar/calendarUtils';
import { ChevronLeftIcon, ChevronRightIcon } from '../../pages/calendar/CalendarIcons';
import { NEIGHBORS_TEXT_DARK } from '../../styles/colors';

type TimeGridMode = Exclude<CalendarLayoutMode, 'month'>;

interface Props {
    mode: TimeGridMode;
    selectedDate: Date;
    todayKey: string;
    selectedKey: string;
    facilityTimezone: string;
    eventsByDate: Map<string, CalendarDisplayEvent[]>;
    onSelectDate: (date: Date) => void;
    onOpenEvent: (event: CalendarDisplayEvent) => void;
    onPrev: () => void;
    onNext: () => void;
}

/** FC needs a concrete color for uncategorized events. */
const UNCATEGORIZED_COLOR = NEIGHBORS_TEXT_DARK;

const civilDateFromKey = (dateKey: string) => {
    const parts = dateKey.split('-');
    return new Date(
        parseInt(parts[0], 10),
        parseInt(parts[1], 10) - 1,
        parseInt(parts[2], 10),
    );
};

const civilKeyFromDate = (date: Date) =>
    getDateKey(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Matches the `cal` screen (1100px) and `md` (768px) in tailwind.config.js — offset by
 * .98px so this never overlaps Tailwind's min-width breakpoint at the exact same pixel
 * (Tailwind's `sm`/`md`/`cal` variants are min-width; these are max-width).
 */
const NARROW_QUERY = '(max-width: 1099.98px)';
const COMPACT_QUERY = '(max-width: 767.98px)';

/**
 * FC's scrollable body needs a literal pixel height, not "auto" or a percentage —
 * FC's own docs call out percentage heights as unreliable since every ancestor has to
 * resolve a definite height for them to work. A literal number sidesteps that entirely.
 */
const useGridLayout = () => {
    const [narrow, setNarrow] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(NARROW_QUERY).matches,
    );
    const [compact, setCompact] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(COMPACT_QUERY).matches,
    );
    useEffect(() => {
        const narrowMql = window.matchMedia(NARROW_QUERY);
        const compactMql = window.matchMedia(COMPACT_QUERY);
        const onChange = () => {
            setNarrow(narrowMql.matches);
            setCompact(compactMql.matches);
        };
        narrowMql.addEventListener('change', onChange);
        compactMql.addEventListener('change', onChange);
        return () => {
            narrowMql.removeEventListener('change', onChange);
            compactMql.removeEventListener('change', onChange);
        };
    }, []);
    return {
        compact,
        height: compact ? 420 : narrow ? 480 : 560,
    };
};

/** Facility wall-clock → local civil Date (FC timeZone="local"; no moment-timezone plugin). */
const toCivilDateTime = (evt: CalendarDisplayEvent, asEnd: boolean) => {
    const parts = evt.dateKey.split('-');
    const startHour = Math.floor(evt.startHourDecimal);
    const startMinute = Math.round((evt.startHourDecimal - startHour) * 60);
    const start = new Date(
        parseInt(parts[0], 10),
        parseInt(parts[1], 10) - 1,
        parseInt(parts[2], 10),
        startHour,
        startMinute,
        0,
        0,
    );
    if (!asEnd) return start;
    return new Date(start.getTime() + evt.durationMinutes * 60 * 1000);
};

const CalendarTimeGrid = ({
    mode,
    selectedDate,
    todayKey,
    selectedKey,
    facilityTimezone,
    eventsByDate,
    onSelectDate,
    onOpenEvent,
    onPrev,
    onNext,
}: Props) => {
    const calendarRef = useRef<FullCalendar>(null);
    const { compact, height: gridHeight } = useGridLayout();
    const weekStart = useMemo(() => getWeekStart(selectedDate), [selectedDate]);

    /** Facility wall-clock "now" relabeled onto the same civil grid as events (see toCivilDateTime). */
    const facilityNow = useCallback(() => {
        const tz = moment.tz(facilityTimezone);
        return new Date(tz.year(), tz.month(), tz.date(), tz.hour(), tz.minute(), tz.second());
    }, [facilityTimezone]);
    const view = mode === 'week' ? 'timeGridWeek' : 'timeGridDay';

    const titleContent = useMemo(() => {
        if (mode === 'week') {
            return (
                <>
                    Week of{' '}
                    <span>
                        {MONTHS_SHORT[weekStart.getMonth()]} {weekStart.getDate()}, {weekStart.getFullYear()}
                    </span>
                </>
            );
        }
        return (
            <>
                {WEEKDAYS[selectedDate.getDay()]},{' '}
                <span>
                    {MONTHS_SHORT[selectedDate.getMonth()]} {selectedDate.getDate()},{' '}
                    {selectedDate.getFullYear()}
                </span>
            </>
        );
    }, [mode, selectedDate, weekStart]);

    const fcEvents = useMemo(
        () => {
            const events: Array<{
                id: string;
                title: string;
                start: Date;
                end: Date;
                backgroundColor: string;
                borderColor: string;
                classNames: string[];
                extendedProps: { displayEvent: CalendarDisplayEvent; };
            }> = [];

            eventsByDate.forEach((dayEvents) => {
                dayEvents.forEach((evt) => {
                    const color = evt.dominantColor || UNCATEGORIZED_COLOR;
                    events.push({
                        id: evt.id,
                        title: evt.title,
                        start: toCivilDateTime(evt, false),
                        end: toCivilDateTime(evt, true),
                        backgroundColor: color,
                        borderColor: color,
                        classNames: [
                            'cal-fc-event',
                            evt.dominantColor ? 'wellness' : 'uncategorized',
                        ],
                        extendedProps: { displayEvent: evt },
                    });
                });
            });

            return events;
        },
        [eventsByDate],
    );

    useEffect(() => {
        const api = calendarRef.current && calendarRef.current.getApi();
        if (!api) return;
        if (api.view.type !== view) {
            api.changeView(view, selectedKey);
        } else {
            api.gotoDate(selectedKey);
        }
    }, [mode, selectedKey, view]);

    const selectCivilDate = (date: Date) => {
        onSelectDate(
            new Date(date.getFullYear(), date.getMonth(), date.getDate()),
        );
    };

    return (
        <div className={`cal-time-view cal-time-view--${mode} min-w-0 w-full max-w-full`}>
            <div className="cal-wk-nav-row mb-nbp-12 flex min-w-0 w-full flex-wrap items-center justify-between gap-nbp-12">
                <div className="cal-wk-title min-w-0 break-words text-nbp-18 sm:text-nbp-20 md:text-nbp-22">{titleContent}</div>
                <div className="cal-nav-arrows flex gap-nbp-10">
                    <button
                        type="button"
                        className="cal-nav-btn h-nbp-36 w-nbp-36 sm:h-nbp-40 sm:w-nbp-40 md:h-nbp-44 md:w-nbp-44"
                        onClick={onPrev}
                        aria-label={mode === 'week' ? 'Previous week' : 'Previous day'}
                    >
                        <ChevronLeftIcon />
                    </button>
                    <button
                        type="button"
                        className="cal-nav-btn h-nbp-36 w-nbp-36 sm:h-nbp-40 sm:w-nbp-40 md:h-nbp-44 md:w-nbp-44"
                        onClick={onNext}
                        aria-label={mode === 'week' ? 'Next week' : 'Next day'}
                    >
                        <ChevronRightIcon />
                    </button>
                </div>
            </div>

            <div className="cal-tgrid-wrap cal-fc-wrap min-w-0 w-full">
                <FullCalendar
                    ref={calendarRef}
                    plugins={[timeGridPlugin, interactionPlugin]}
                    initialView={view}
                    initialDate={selectedKey}
                    headerToolbar={false}
                    height={gridHeight}
                    stickyHeaderDates
                    scrollTime="08:00:00"
                    allDaySlot={false}
                    nowIndicator
                    now={facilityNow}
                    slotMinTime="00:00:00"
                    slotMaxTime="24:00:00"
                    slotDuration="01:00:00"
                    slotLabelInterval="01:00:00"
                    slotLabelFormat={{
                        hour: 'numeric',
                        minute: '2-digit',
                        omitZeroMinute: true,
                        meridiem: 'short',
                    }}
                    dayHeaderFormat={
                        mode === 'day'
                            ? { weekday: compact ? 'short' : 'long', day: 'numeric' }
                            // 'narrow' collapses to a single letter (Sun/Sat both "S", Tue/Thu
                            // both "T") — ambiguous, and these headers are click targets
                            // (navLinks) in week mode, so keep 'short' and let the existing
                            // 768px/480px LESS font-size rules shrink it instead.
                            : { weekday: 'short', day: 'numeric' }
                    }
                    weekends
                    firstDay={0}
                    timeZone="local"
                    events={fcEvents}
                    editable={false}
                    selectable={false}
                    navLinks={mode === 'week'}
                    navLinkDayClick={selectCivilDate}
                    dateClick={(info) => {
                        if (mode !== 'week') return;
                        onSelectDate(civilDateFromKey(info.dateStr.slice(0, 10)));
                    }}
                    eventClick={(info) => {
                        info.jsEvent.preventDefault();
                        const displayEvent = info.event.extendedProps
                            .displayEvent as CalendarDisplayEvent | undefined;
                        if (displayEvent) onOpenEvent(displayEvent);
                    }}
                    dayHeaderClassNames={(arg) => {
                        const dk = civilKeyFromDate(arg.date);
                        const classes = ['cal-fc-day-head'];
                        if (dk === todayKey) classes.push('today');
                        if (dk === selectedKey && dk !== todayKey) classes.push('selected');
                        return classes;
                    }}
                    dayCellClassNames={(arg) => {
                        const dk = civilKeyFromDate(arg.date);
                        const classes = ['cal-fc-day-cell'];
                        if (dk === todayKey) classes.push('today');
                        if (dk === selectedKey) classes.push('selected');
                        return classes;
                    }}
                    eventContent={(arg) => {
                        const displayEvent = arg.event.extendedProps
                            .displayEvent as CalendarDisplayEvent | undefined;
                        const timeLabel = displayEvent
                            ? `${displayEvent.time} ${displayEvent.ampm}`
                            : '';
                        return (
                            <Popup
                                trigger={
                                    <div className="cal-fc-event-inner">
                                        <div className="cal-fc-event-title">{arg.event.title}</div>
                                        {timeLabel ? (
                                            <div className="cal-fc-event-time">{timeLabel}</div>
                                        ) : null}
                                    </div>
                                }
                                content={
                                    <div className="cal-fc-event-popup">
                                        <div className="cal-fc-event-popup-title">{arg.event.title}</div>
                                        {timeLabel && (
                                            <div className="cal-fc-event-popup-line">{timeLabel}</div>
                                        )}
                                        {displayEvent && displayEvent.location ? (
                                            <div className="cal-fc-event-popup-line">{displayEvent.location}</div>
                                        ) : null}
                                    </div>
                                }
                                position="top center"
                                size="tiny"
                                mouseEnterDelay={200}
                                hoverable
                            />
                        );
                    }}
                />
            </div>
        </div>
    );
};

export default CalendarTimeGrid;
